import { DrawerContentWrapper, Drawer } from "../../../shared/ui/Drawer";
import { useDrawerResizeObserver } from "../model/useDrawerResizeObserver";
import { NavigationContent } from "./nav-content";

interface NavDrawerProps {
    open: boolean;
    isMobile: boolean;
    onDrawerToggle: () => void;
    onWidthChange: (width: number) => void;
    children?: React.ReactNode;
}

/**
 * NavDrawer
 *
 * Responsive navigation drawer component. Handles both mobile (temporary) and desktop (permanent) modes.
 * Measures and reports drawer width using a resize observer. Renders navigation content and children.
 *
 * @param open - Whether the drawer is open (for mobile)
 * @param isMobile - Whether the layout is in mobile mode
 * @param onDrawerToggle - Callback to toggle the drawer open/closed
 * @param onWidthChange - Callback to report the drawer width
 * @param children - Drawer content
 */
export const NavDrawer: React.FC<NavDrawerProps> = ({
    open,
    isMobile,
    onDrawerToggle,
    onWidthChange,
    children,
}) => {
    const { drawerRef } = useDrawerResizeObserver({
        isMobile,
        onWidthChange,
        children,
    });

    const drawerContent = (
        <NavigationContent
            ref={drawerRef}
            isMobile={isMobile}
            onDrawerToggle={onDrawerToggle}
        >
            {children}
        </NavigationContent>
    );

    // Mobile: Temporary drawer
    if (isMobile) {
        return (
            <Drawer
                variant="temporary"
                open={open}
                onClose={onDrawerToggle}
                isMobile={isMobile}
                ModalProps={{
                    keepMounted: true,
                }}
            >
                <DrawerContentWrapper>{drawerContent}</DrawerContentWrapper>
            </Drawer>
        );
    }

    // Desktop: Permanent drawer
    return (
        <Drawer variant="permanent" isMobile={isMobile}>
            <DrawerContentWrapper>{drawerContent}</DrawerContentWrapper>
        </Drawer>
    );
};
